import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {LoadingButton} from "@mui/lab";
import {Box, IconButton, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import UnsavedChangesDialog from "crqlar-react-common/dist/components/UnsavedChangesDialog";
import {
    Survey,
    SurveyContentAlignment,
    SurveyFormat,
    SurveyTheme
} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useUnsavedChangesWarning} from "crqlar-react-common/dist/utils/useUnsavedChangesWarning";
import {isEqual} from "lodash";
import {enqueueSnackbar} from "notistack";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useNavigate, useParams} from "react-router-dom";
import {RichTextActiveEditorProvider} from "../../common/newRichText";
import SurveyRunner from "../runner/SurveyRunner";
import SurveyRunnerThemeProvider from "../runner/SurveyRunnerThemeProvider";
import {useSurveyChangeRefresh} from "../shared/surveyQueryRefresh";
import SurveyEditorCanvas from "./SurveyEditorCanvas";
import SurveyEditorLeftPanel, {LeftPanelTab} from "./SurveyEditorLeftPanel";
import SurveyQuestionDetailsPanel from "./SurveyQuestionDetailsPanel";
import {scaleLabelsVisible} from "./scaleLabels.util";
import {useFullscreenSnackbarAnchor} from "../../layout/DashboardSnackbarProvider";

type EditorMode = 'build' | 'preview';

type SectionTitleFocus = {questionId: string, nonce: number};

const SIDE_COLUMN_WIDTH = 300;

const CANVAS_MAX_WIDTH = 760;

function SurveyEditor() {
    useTrackScreenVisit('system survey editor');
    useFullscreenSnackbarAnchor();

    const {id: surveyId} = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const organization = useOrganization();
    const refreshSurveys = useSurveyChangeRefresh();

    const [draft, setDraft] = useState<Survey | undefined>(undefined);
    const [saved, setSaved] = useState<Survey | undefined>(undefined);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | undefined>(undefined);
    /*
        A divider click asks the canvas to focus the section heading field, which serves each number once, on mount
        too. The nonce only grows, so the next click focuses the field again; any other selection, list change,
        format switch or leaving Build clears the request, so a heading field that mounts later never takes focus.
    */
    const [sectionTitleFocus, setSectionTitleFocus] = useState<SectionTitleFocus | undefined>(undefined);
    const sectionTitleFocusNonce = useRef(0);
    const [mode, setMode] = useState<EditorMode>('build');
    const [leftTab, setLeftTab] = useState<LeftPanelTab>('questions');
    const [editingLanguage, setEditingLanguage] = useState<string | undefined>(undefined);
    const [scaleLabelOverrides, setScaleLabelOverrides] = useState<Record<string, boolean>>({});
    const [busy, setBusy] = useState(false);

    const surveyQuery = useQuery(
        ['survey', surveyId],
        () => SurveyService.getSurveyById(surveyId!),
        {enabled: !!surveyId, refetchOnWindowFocus: false}
    );

    useEffect(() => {
        if (!surveyQuery.data) return;
        setDraft(surveyQuery.data);
        setSaved(surveyQuery.data);
    }, [surveyQuery.data]);

    const questions = useMemo(() => draft?.questions ?? [], [draft?.questions]);
    const languages = useMemo(() => draft?.languages ?? [], [draft?.languages]);

    const language = editingLanguage && languages.includes(editingLanguage)
        ? editingLanguage
        : languages[0] ?? 'en';

    useEffect(() => {
        if (!questions.length) {
            setSelectedQuestionId(undefined);
            return;
        }
        if (selectedQuestionId && questions.some(question => question.id === selectedQuestionId)) return;
        setSelectedQuestionId(questions[0].id);
    }, [questions, selectedQuestionId]);

    const selectedQuestion = questions.find(question => question.id === selectedQuestionId);
    const sectionTitleFocusKey = sectionTitleFocus && sectionTitleFocus.questionId === selectedQuestionId
        ? sectionTitleFocus.nonce
        : undefined;
    const showScaleLabels = scaleLabelsVisible(selectedQuestion, scaleLabelOverrides);
    const isDirty = !!draft && !!saved && !isEqual(draft, saved);
    const editingDisabled = readOnly || busy;

    const {showDialog, handleConfirmNavigation, handleCancelNavigation} = useUnsavedChangesWarning({
        hasUnsavedChanges: isDirty && !readOnly,
        isSaving: busy,
    });

    const patchSurvey = (partial: Partial<Survey>) =>
        setDraft(current => (current ? {...current, ...partial} : current));

    const updateQuestion = (updated: SurveyQuestion) =>
        setDraft(current => {
            if (!current) return current;
            return {
                ...current,
                questions: (current.questions ?? []).map(question =>
                    question.id === updated.id ? updated : question
                ),
            };
        });

    const selectQuestion = (questionId: string) => {
        setSectionTitleFocus(undefined);
        setSelectedQuestionId(questionId);
    };

    // A divider click: the section's first question, with its heading field focused.
    const selectSection = (questionId: string) => {
        sectionTitleFocusNonce.current += 1;
        setSectionTitleFocus({questionId, nonce: sectionTitleFocusNonce.current});
        setSelectedQuestionId(questionId);
    };

    const changeQuestions = (next: SurveyQuestion[]) => {
        setSectionTitleFocus(undefined);
        patchSurvey({questions: next});
    };

    const changeFormat = (format: SurveyFormat) => {
        setSectionTitleFocus(undefined);
        patchSurvey({format});
    };

    const changeMode = (next: EditorMode) => {
        setSectionTitleFocus(undefined);
        setMode(next);
    };

    const notifyError = () => enqueueSnackbar(t('Changes could not be saved. Check all fields!'), {
        variant: 'error',
    });

    const notifySaved = () => enqueueSnackbar(t('Changes saved!'), {
        variant: 'success',
    });

    const saveDraft = async (): Promise<Survey | undefined> => {
        if (!draft) return undefined;

        const result = await SurveyService.updateSurvey(draft);
        setDraft(result);
        setSaved(result);
        void refreshSurveys();
        return result;
    };

    const handleSave = async () => {
        setBusy(true);
        try {
            await saveDraft();
            notifySaved();
        } catch (e) {
            console.log(e);
            notifyError();
        } finally {
            setBusy(false);
        }
    };

    const header = (
        <Stack
            direction={'row'}
            alignItems={'center'}
            gap={1}
            sx={{
                px: 2,
                minHeight: 56,
                py: {xs: 1, md: 0},
                flexShrink: 0,
                flexWrap: {xs: 'wrap', md: 'nowrap'},
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}
        >
            <IconButton aria-label={t('Back')} onClick={() => navigate(`/system/surveys/${surveyId}`)}>
                <ArrowBackIcon/>
            </IconButton>

            <Typography variant={'h6'} noWrap>
                {t('Editor')}
            </Typography>

            {!!draft?.name && (
                <Typography variant={'body1'} color={'text.secondary'} noWrap>
                    {draft.name}
                </Typography>
            )}

            <Box sx={{flex: 1}}/>

            <NewStatusPicker<EditorMode>
                value={mode}
                onChange={changeMode}
                ariaLabel={t('Editor mode')}
                options={[
                    {value: 'build', label: t('Build')},
                    {value: 'preview', label: t('Preview')},
                ]}
            />

            <LoadingButton
                variant={'contained'}
                loading={busy}
                disabled={!isDirty || readOnly}
                onClick={handleSave}
            >
                {t('Save')}
            </LoadingButton>
        </Stack>
    );

    const sideColumnSx = {
        width: {xs: '100%', lg: SIDE_COLUMN_WIDTH},
        flexShrink: 0,
        minHeight: 0,
    } as const;

    const canvasAlignmentSx = draft?.theme?.contentAlignment === SurveyContentAlignment.CENTER
        ? {my: 'auto'}
        : draft?.theme?.contentAlignment === SurveyContentAlignment.BOTTOM
            ? {mt: 'auto'}
            : {};

    const buildMode = draft && (
        <Stack direction={{xs: 'column', lg: 'row'}} sx={{flex: 1, minHeight: 0}}>
            <Box
                sx={theme => ({
                    ...sideColumnSx,
                    borderRight: {xs: 'none', lg: `1px solid ${theme.palette.divider}`},
                    borderBottom: {xs: `1px solid ${theme.palette.divider}`, lg: 'none'},
                })}
            >
                <SurveyEditorLeftPanel
                    survey={draft}
                    questions={questions}
                    language={language}
                    selectedQuestionId={selectedQuestionId}
                    tab={leftTab}
                    onTabChange={setLeftTab}
                    onSelectQuestion={selectQuestion}
                    onSelectSection={selectSection}
                    onQuestionsChange={changeQuestions}
                    onFormatChange={changeFormat}
                    onThemeChange={(theme: SurveyTheme) => patchSurvey({theme})}
                    disabled={editingDisabled}
                />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    overflowY: {lg: 'auto'},
                    p: {xs: 2, md: 3},
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{width: '100%', maxWidth: CANVAS_MAX_WIDTH, mx: 'auto', ...canvasAlignmentSx}}>
                    <SurveyEditorCanvas
                        survey={draft}
                        question={selectedQuestion}
                        language={language}
                        scaleLabelsVisible={showScaleLabels}
                        onChange={updateQuestion}
                        disabled={editingDisabled}
                        sectionTitleFocusKey={sectionTitleFocusKey}
                    />
                </Box>
            </Box>

            <Box
                sx={theme => ({
                    ...sideColumnSx,
                    borderLeft: {xs: 'none', lg: `1px solid ${theme.palette.divider}`},
                    borderTop: {xs: `1px solid ${theme.palette.divider}`, lg: 'none'},
                })}
            >
                <SurveyQuestionDetailsPanel
                    question={selectedQuestion}
                    questions={questions}
                    languages={languages}
                    language={language}
                    onLanguageChange={setEditingLanguage}
                    onChange={updateQuestion}
                    scaleLabelsVisible={showScaleLabels}
                    onScaleLabelsVisibleChange={visible => {
                        if (!selectedQuestionId) return;
                        setScaleLabelOverrides(current => ({...current, [selectedQuestionId]: visible}));
                    }}
                    disabled={editingDisabled}
                />
            </Box>
        </Stack>
    );

    const previewMode = draft && (
        <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column'}}>
            <SurveyRunnerThemeProvider theme={draft.theme}>
                <SurveyRunner
                    survey={draft}
                    language={language}
                    onLanguageChange={setEditingLanguage}
                    organizationName={organization?.name}
                    initialQuestionId={selectedQuestionId}
                />
            </SurveyRunnerThemeProvider>
        </Box>
    );

    return (
        <Box sx={{height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default'}}>
            {header}

            {surveyQuery.isLoading || !draft ? (
                <DetailsLoading/>
            ) : mode === 'build' ? (
                // Lets the right column follow the chip selected in the canvas' title or description.
                <RichTextActiveEditorProvider>{buildMode}</RichTextActiveEditorProvider>
            ) : (
                previewMode
            )}

            <UnsavedChangesDialog
                open={showDialog}
                onConfirm={handleConfirmNavigation}
                onCancel={handleCancelNavigation}
            />
        </Box>
    );
}

export default SurveyEditor;
